<template>
  <div class="modal-content">
    <div class="modal-header">
      <h4 class="modal-title">{{ _('User') }}</h4>
    </div>
    <div class="modal-body" style="min-height: 500px;">
      <editor-tabs id="tabNavi" :tabs="tabList" :selected="selectedTab" :id-prefix="tabIdPrefix" v-on:select="tab" />
      <div class="tab-content">
        <transition-group name="fade">
          <div v-bind="tabPanel('general')" class="tab-pane active" id="general" v-if="isTabActive('general')" key="general">
            <div class="row">
              <div class="col-md-12">
                <div class="form-group">
                  <label for="userName">{{ _('Name') }}</label>
                  <input type="text" class="form-control" id="userName" :placeholder="_('Enter the user\'s full name')" v-model="user.name" />
                </div>
              </div>
            </div>
            <div class="row" v-if="isNewUser">
              <div class="col-md-12">
                <div class="form-group">
                  <label>{{ _('Account type') }}</label>
                  <div class="radio">
                    <label>
                      <input class="radio" type="radio" name="accountType" value="classic" v-model="accountType" />
                      {{ _('Standard user (email sign-in)') }}
                    </label>
                  </div>
                  <div class="radio">
                    <label>
                      <input class="radio" type="radio" name="accountType" value="staticLink" v-model="accountType" :disabled="!staticTokenEligible" />
                      {{ _('Direct sign-in link (no email required)') }}
                    </label>
                  </div>
                  <p class="help-block" v-if="isCreatingStaticLinkUser">{{ _('This user will sign in using a direct link instead of an email. A shop-specific placeholder email will be generated automatically once the user is created, and it can\'t be used for logging in.') }}</p>
                  <p class="help-block" v-else-if="!staticTokenEligible">{{ _('Direct sign-in links aren\'t available for Administrators.') }}</p>
                </div>
              </div>
            </div>
            <div class="row" v-if="!isCreatingStaticLinkUser">
              <div class="col-md-12">
                <div class="form-group">
                  <label for="userEmail">{{ _('Email') }}</label>
                  <input autocomplete="off" type="email" class="form-control" id="userEmail" :placeholder="_('Enter the user\'s email')" v-model="user.email" :readonly="isEmailLocked" />
                  <p class="help-block" v-if="isEmailLocked">{{ _('This user has an active direct sign-in link, so their email can\'t be edited here. Disable the link first if you need to change it.') }}</p>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-12">
                <div class="form-group">
                  <label for="userLevel">{{ _('User level') }}</label>
                  <select id="userLevel" v-model="user.userlevel" class="form-control">
                    <option value="1" v-if="user.userlevel == 1">{{ _('Private sales channel user') }}</option>
                    <option value="2">{{ _('POS user') }}</option>
                    <option value="3">{{ _('Standard user') }}</option>
                    <option value="7" v-if="userData && parseInt(userData.userlevel, 10) >= 7">{{ _('Administrator') }}</option>
                    <option v-else disabled="disabled" value="7">{{ _('Administrator') }}</option>
                    <option v-if="user.userlevel == 10" value="10" disabled="disabled">{{ _('System Administrator') }}</option>
                  </select>
                </div>
              </div>
            </div>
            <div class="row" v-if="isAccountingConfigured">
              <div class="col-md-12">
                <div class="form-group">
                  <label for="dimensions">{{ _('Cost objects') }}</label>
                  <draggable-multi-selector v-model="user.sharedDimension" :items="allDimensions" :groups="true" :single-select="isBrioxAccountingMode" itemProperty="name" :selectorTitle="_('Link a Cost object')" />
                </div>
              </div>
            </div>
            <div class="row" v-if="!showAdvancedSettings && !hasStaticLoginLink">
              <div class="col-md-12">
                <a href="javascript:;" v-on:click="showAdvancedSettings = true">{{ _('Show all details') }}</a>
              </div>
            </div>
            <div class="row" v-if="showAdvancedSettings && !hasStaticLoginLink">
              <div class="col-md-12">
                <hr/>
                <div class="form-group">
                  <label for="userPassword">{{ _('Application password') }}</label>
                  <input autocomplete="new-password" type="password" class="form-control" id="userPassword" placeholder="" v-model="user.password" />
                </div>
              </div>
            </div>
            <div class="row" v-if="showAdvancedSettings && !hasStaticLoginLink">
              <div class="col-md-12">
                <div class="form-group">
                  <label for="userConfirmPassword">{{ _('Confirm application password') }}</label>
                  <input autocomplete="new-password" type="password" class="form-control" id="userConfirmPassword" placeholder="" v-model="user.confirmpassword" />
                </div>
                <p class="help-block">{{ _('The application password is used to authenticate the user to Johku Printer, Johku Reader and similar applications.') }}</p>
                <hr/>
              </div>
            </div>
            <div class="row" v-if="showAdvancedSettings || hasStaticLoginLink">
              <div class="col-md-12">
                <div class="form-group">
                  <label for="userApiKey">{{ _('API key') }}</label>
                  <input type="text" class="form-control" id="userApiKey" :placeholder="_('The API key will be automatically generated on save')" :value="user.apikey" readonly />
                </div>
              </div>
            </div>
            <div class="row" v-if="showAdvancedSettings || hasStaticLoginLink">
              <div class="col-md-12">
                <button class="btn btn-warning" v-on:click="resetApiKey" type="button" id="resetButton" v-if="isResetButtonVisible" :disabled="!isResetButtonEnabled"><i class="fa fa-fw fa-arrows-retweet"></i> {{ _('Reset API key') }}</button>
              </div>
            </div>
            <div class="row" v-if="showStaticTokenSection && (showAdvancedSettings || hasStaticLoginLink)">
              <div class="col-md-12">
                <hr/>
                <div class="form-group">
                  <label>{{ _('Direct sign-in link') }}</label>
                  <template v-if="!staticTokenEligible">
                    <p class="help-block">{{ _('Direct sign-in links are only available for POS and Standard users, not Administrators.') }}</p>
                  </template>
                  <template v-else-if="staticTokenLoading">
                    <p><i class="fa fa-spinner fa-spin"></i> {{ _('Loading...') }}</p>
                  </template>
                  <template v-else-if="staticToken">
                    <div class="input-group">
                      <input type="text" class="form-control" readonly :value="staticTokenLink" />
                      <span class="input-group-btn">
                        <button class="btn btn-default" type="button" v-on:click="copyStaticTokenLink"><i class="fa fa-fw fa-copy"></i></button>
                      </span>
                    </div>
                    <p class="help-block">{{ _('Anyone with this link can sign in as this user on any device - share it only with the intended person.') }}</p>
                    <div class="btn-group" style="margin-top: 5px">
                      <button class="btn btn-warning btn-sm" type="button" :disabled="staticTokenActionPending" v-on:click="generateStaticToken"><i class="fa fa-fw fa-arrows-retweet"></i> {{ _('Reroll') }}</button>
                      <button class="btn btn-danger btn-sm" type="button" :disabled="staticTokenActionPending" v-on:click="disableStaticToken"><i class="fa fa-fw fa-ban"></i> {{ _('Disable') }}</button>
                    </div>
                  </template>
                  <template v-else>
                    <p class="help-block">{{ _('This user currently signs in with an email. Converting them to a direct sign-in link is a one-way switch of their sign-in method - see the confirmation for details.') }}</p>
                    <button class="btn btn-success btn-sm" type="button" :disabled="staticTokenActionPending" v-on:click="confirmConvertToStaticLink"><i class="fa fa-fw fa-key"></i> {{ _('Convert to a direct sign-in link') }}</button>
                  </template>
                </div>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('userinterfaces')" class="tab-pane active" id="userinterfaces" v-if="isTabActive('userinterfaces')" key="userinterfaces">
            <div class="row">
              <div class="col-md-12">
                <h3>{{ _('Linked user interfaces') }}</h3>
                <draggable-multi-selector v-model="user.sharedClient" :items="allClients" itemProperty="name" :selectorTitle="_('Link a user interface')" />
              </div>
            </div>
            <div class="row" v-if="!user.sharedClient || !user.sharedClient.length">
              <div class="col-md-12">
                <div class="form-group">
                  <div class="checkbox">
                    <label>
                      <input type="checkbox" v-model="makeNewUserinterface" :true-value="true" :false-value="false" />
                      {{ _('Create a new User interface and Point of Use') }}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('regional')" class="tab-pane active" id="regional" v-if="isTabActive('regional')" key="regional">
            <div class="row">
              <div class="col-md-12">
                <h3>{{ _('Regional settings') }}</h3>
              </div>
            </div>
            <div class="row">
              <div class="col-md-12">
                <div class="form-group">
                  <h4>{{ _('Language') }}</h4>
                  <p />
                  <div class="radio">
                    <label>
                      <input class="radio" type="radio" name="locale" :value="null" v-model="user.language" /> {{ _('System default') }}
                    </label>
                  </div>
                  <div class="radio">
                    <label>
                      <input class="radio" type="radio" name="locale" rel="fi" value="fi_FI" v-model="user.language" /> Suomi
                    </label>
                  </div>
                  <div class="radio">
                    <label>
                      <input class="radio" type="radio" name="locale" rel="en" value="en_US" v-model="user.language" /> English
                    </label>
                  </div>
                  <div class="radio">
                    <label>
                      <input class="radio" type="radio" name="locale" rel="sv" value="sv_SE" v-model="user.language" /> Svenska
                    </label>
                  </div>
                  <div class="radio">
                    <label>
                      <input class="radio" type="radio" name="locale" rel="it" value="it_IT" v-model="user.language" /> Italiano
                    </label>
                  </div>
                </div>
                <div class="form-group">
                  <h4>{{ _('Date format') }}</h4>
                  <select id="dateFormat" name="dateformat" class="form-control" v-model="user.dateformat">
                    <option :value="null">{{ _('System default') }}</option>
                    <option v-for="format in dateFormats" :value="format">{{ DateTime.fromISO('2034-12-31').toFormat(toLuxonFormat(format)) }}</option>
                  </select>
                </div>
                <div class="form-group">
                  <h4>{{ _('Time format') }}</h4>
                  <select id="timeFormat" name="timeformat" class="form-control" v-model="user.timeformat">
                    <option :value="null">{{ _('System default') }}</option>
                    <option v-for="format in timeFormats" :value="format">{{ DateTime.fromISO('2023-12-31T23:59').toFormat(toLuxonFormat(format)) }}</option>
                  </select>
                </div>
                <div class="form-group">
                  <h4>{{ _('Number format') }}</h4>
                  <select id="numberFormat" name="numberformat" class="form-control" v-model="user.numberformat">
                    <option :value="null">{{ _('System default') }}</option>
                    <option v-for="format in numberFormats" :value="format">{{ formatNumber(1234.56, format) }}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </transition-group>
      </div>
    </div>
    <div class="modal-footer">
      <div class="flex-row space-between">
        <button class="btn btn-danger" v-on:click="remove" type="button" id="removeButton" v-if="isRemoveButtonVisible"><i class="fa fa-fw fa-trash"></i> {{ _('Delete') }}</button>
        <div class="button-col">
          <button class="btn btn-default" v-on:click="hideModal"><i class="fa fa-fw fa-close"></i> {{ _('Close') }}</button>
          <button class="btn btn-success" v-on:click="save" :disabled="!canSave"><i class="fa fa-save fa-fw"></i> {{ _('Save') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import errors from '~/lib/errors'
import formats from '~/lib/formats'
import modals from '~/lib/modals'
import settingshelpers from '~/lib/settingshelpers'
import tabs from '~/lib/tabs'
import { DraggableMultiSelector } from '#components'
import { mapGetters } from 'vuex'
import { DateTime } from 'luxon'

export default {
  mixins: [errors, formats, modals, settingshelpers, tabs],
  props: ['originalUser', 'baseDomain', 'shopId', 'apiKey', 'settings'],
  data () {
    return {
      user: {},
      allDimensions: [],
      allClients: [],
      makeNewUserinterface: false,
      showAdvancedSettings: false,
      DateTime: DateTime,
      staticToken: null,
      staticTokenLoading: false,
      staticTokenActionPending: false,
      accountType: 'classic'
    }
  },
  computed: {
    tabList () {
      return this.editorTabs([
        { id: 'general', label: this._('General') },
        { id: 'userinterfaces', label: this._('User interfaces') },
        { id: 'regional', label: this._('Regional settings') }
      ])
    },
    ...mapGetters(['userData']),
    shortLocale () {
      return this.locale.split('_')[0]
    },
    canSave () {
      if (!this.user || !this.user.name || !this.user.userlevel) {
        return false
      }
      if (this.isCreatingStaticLinkUser) {
        return this.staticTokenEligible
      }
      return !!this.user.email && (!this.user.password || (this.user.password === this.user.confirmpassword))
    },
    isNewUser () {
      return !this.user || !parseInt(this.user.id, 10)
    },
    isCreatingStaticLinkUser () {
      return this.isNewUser && this.accountType === 'staticLink'
    },
    // While a direct sign-in link is active, the email is a generated
    // placeholder the link is keyed on - lock it here to match the backend,
    // which rejects changing it out from under an active link.
    isEmailLocked () {
      return !!this.staticToken
    },
    // True for both an existing user with an active link and a new user being
    // created as one - either way they sign in with the link, not a password,
    // so the application password fields (and the toggle to reveal them)
    // don't apply.
    hasStaticLoginLink () {
      return this.isEmailLocked || this.isCreatingStaticLinkUser
    },
    isResetButtonEnabled () {
      return this.user && parseInt(this.user.id) && JSON.stringify(this.originalUser) === JSON.stringify(this.user)
    },
    isResetButtonVisible () {
      return this.user && parseInt(this.user.id)
    },
    isRemoveButtonVisible () {
      return this.user && parseInt(this.user.id)
    },
    isAdmin () {
      return this.userData && parseInt(this.userData.userlevel, 10) >= 7
    },
    // The static-token link section is only ever meaningful for an existing user
    // (the token is issued against an already-saved account) and only ever
    // manageable by an Administrator-level viewer - both are re-checked
    // server-side on every management call regardless of this UI gate.
    showStaticTokenSection () {
      return this.isAdmin && this.user && parseInt(this.user.id)
    },
    staticTokenEligible () {
      return this.user && parseInt(this.user.userlevel, 10) < 7
    },
    staticTokenLink () {
      if (!this.staticToken || typeof window === 'undefined') {
        return ''
      }
      return window.location.origin + '/' + this.shopId + '/t#' + this.staticToken
    }
  },
  methods: {
    toLuxonFormat (format) {
      return format.replace(/YYYY/g, 'yyyy').replace(/YY/g, 'yy').replace(/MMMM/g, 'LLLL').replace(/MMM/g, 'LLL').replace(/MM/g, 'LL').replace(/M/g, 'L').replace(/DD/g, 'dd').replace(/D/g, 'd')
    },
    hideModal () {
      this.$emit('close')
    },
    save () {
      if (this.makeNewUserinterface) {
        this.user.makenewclient = true
      }
      if (this.isCreatingStaticLinkUser) {
        // No real email/password is collected for this account type - the
        // backend generates the shop-scoped placeholder email and issues the
        // token itself once the user has been created.
        this.user.staticLoginLink = true
        delete this.user.email
        delete this.user.password
        delete this.user.confirmpassword
      }
      this.$emit('save', this.user)
    },
    remove () {
      this.$emit('remove', this.user)
    },
    async resetApiKey () {
      this.$emit('reset', this.user)
    },
    async loadClients () {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/clients.json?available=true&apikey=' + this.apiKey)
      } catch(e) {
        return false
      }
      this.allClients = response.data
    },
    async loadDimensions () {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/dimensions.json?apikey=' + this.apiKey)
      } catch(e) {
        return false
      }
      let dim = response.data
      let dimensions = []
      for (let i = 0; i < dim.length; i++) {
        if (parseInt(dim[i].level) === 0) {
          let currParent = dim[i]
          let currChildren = []
          for (let j = 0; j < dim.length; j++) {
            if (dim[j].parentid === currParent.id) {
              currChildren.push(dim[j])
            }
          }
          currParent.children = currChildren
          dimensions.push(currParent)
        }
      }
      this.allDimensions = dimensions
    },
    async loadStaticTokenStatus () {
      this.staticTokenLoading = true
      try {
        const response = await axios.get(this.baseDomain + this.shopId + '/users/' + this.user.id + '.json?statictokenstatus=1&apikey=' + this.apiKey)
        this.staticToken = response.data && response.data.token ? response.data.token : null
      } catch (e) {
        this.staticToken = null
      } finally {
        this.staticTokenLoading = false
      }
    },
    confirmConvertToStaticLink () {
      this.showDialog({
        title: this._('Convert to a direct sign-in link?'),
        text: this._('This replaces the user\'s email address with a shop-specific placeholder, so they\'ll no longer be able to sign in with an email - only with the generated link. You can restore email sign-in later by editing their email address.'),
        width: 600,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Convert'),
            class: 'success',
            icon: 'key',
            handler: this.generateStaticToken
          }
        ]
      })
    },
    async generateStaticToken () {
      this.staticTokenActionPending = true
      try {
        const response = await axios.patch(this.baseDomain + this.shopId + '/users/' + this.user.id + '.json?apikey=' + this.apiKey, {
          id: this.user.id,
          action: 'generateStaticLoginToken'
        })
        this.staticToken = response.data && response.data.token ? response.data.token : null
        if (response.data && response.data.email) {
          // The backend replaces this user's email with a shop-scoped placeholder
          // the first time a link is generated - reflect that here so the visible
          // Email field doesn't show a stale value until the modal is reopened.
          this.user.email = response.data.email
        }
        // The underlying user list (rendered by the parent) still has the old
        // email cached - let it know without closing this modal, so the admin
        // can keep copying the link.
        this.$emit('staticTokenChanged', this.user)
        this.showNotification(this._('A new direct sign-in link has been generated. Any previous link has stopped working.'))
      } catch (e) {
        this.showError(e)
      } finally {
        this.staticTokenActionPending = false
      }
    },
    async disableStaticToken () {
      this.staticTokenActionPending = true
      try {
        await axios.patch(this.baseDomain + this.shopId + '/users/' + this.user.id + '.json?apikey=' + this.apiKey, {
          id: this.user.id,
          action: 'disableStaticLoginToken'
        })
        this.staticToken = null
        this.showNotification(this._('The direct sign-in link has been disabled.'))
      } catch (e) {
        this.showError(e)
      } finally {
        this.staticTokenActionPending = false
      }
    },
    async copyStaticTokenLink () {
      try {
        await this.$copyText(this.staticTokenLink)
        this.showNotification(this._('Sign-in link copied to clipboard!'))
      } catch (e) {
        this.showError(e)
      }
    }
  },
  components: {
    DraggableMultiSelector
  },
  watch: {
    'user.userlevel' (value) {
      // Direct sign-in links aren't available for Administrators - if the level
      // is bumped up past that while still creating the user, fall back to a
      // standard account rather than leaving an invalid choice selected.
      if (this.accountType === 'staticLink' && !(parseInt(value, 10) < 7)) {
        this.accountType = 'classic'
      }
    }
  },
  created() {
    if (this.originalUser) {
      this.user = Object.assign({}, this.originalUser)
    }
    if (!this.user || !parseInt(this.user.id)) {
      this.makeNewUserinterface = true
    }
    this.loadClients()
    this.loadDimensions()
    if (this.showStaticTokenSection && this.staticTokenEligible) {
      this.loadStaticTokenStatus()
    }
  }
}
</script>